import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  StorableSummaryFromStorableIdQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import StorableStore from 'samples/StorableStore';

/**
 * Storable summaries loaded through the async custom resolution system.
 *
 * Evaluation must never read storables straight out of the StorableStore: what the store holds
 * depends on which parts of the app the browser happened to load, so the same table values can
 * evaluate differently in the browser, in the evaluation service, and across evaluation passes.
 * Going through `CustomResolutionQueryType.STORABLE_SUMMARY_FROM_STORABLE_ID` moves that read into
 * the async half of the resolution system, where its outcome is recorded as a query resolution. A
 * later evaluation pass reads the recording rather than the store.
 */

export function makeStorableSummaryFromStorableIdQuery(
  storableId: string
): StorableSummaryFromStorableIdQuery {
  return {
    type: ResolutionQueryType.CUSTOM,
    customResolutionQueryType: CustomResolutionQueryType.STORABLE_SUMMARY_FROM_STORABLE_ID,
    storableId,
  };
}

/**
 * Returns a STORABLES typed value per query, holding the requested storable's summary as the
 * StorableStore has it. A storable the store does not hold resolves to no typed value, which the
 * caller treats the way it used to treat a store miss: by resolving the link's text instead.
 */
export function bulkResolveStorableSummariesFromStore(
  queries: Array<StorableSummaryFromStorableIdQuery>
): Promise<Array<ServerResolutionResult>> {
  return Promise.resolve(
    queries.map((query) => {
      const storableSummary = StorableStore.getStorableSummary(query.storableId);
      return makeCustomResolutionResultFromTypedValue(
        storableSummary != null
          ? {
              type: BaseTypedValueType.STORABLES,
              storableSummaries: [
                {id: storableSummary.id, name: storableSummary.name, barcode: storableSummary.barcode},
              ],
            }
          : null
      );
    })
  );
}
